import {utcToLocalDateTimeString, utcToLocalMMDDYYYY, utcToLocalMMDDYYYYWithTime} from "../reducers/dateTimeHelpers";
import _ from "lodash";
import isEmpty from "../helpers/isEmpty";

class ModelMapperService {

    mapCommunicationDetail = comm => {
        const {deliveries, includesPoll, poll, event, availableEvents} = comm;
        const contactMethodCounts = _.countBy(deliveries, 'contactMethodName');
        const mappedRecipients = this.mapCommunicationRecipients(deliveries, includesPoll, poll);

        const mappedComm =  {
            id: comm.id,
            title: comm.title,
            message: comm.messageText,
            status: comm.statusString,
            creatorName: comm.creatorName,
            dateCreatedString: utcToLocalDateTimeString(comm.createdDate),
            sentDateString: utcToLocalDateTimeString(comm.sentDate),
            includesPoll: includesPoll,
            poll: includesPoll ? this.mapCommunicationPoll(poll, mappedRecipients) : null,
            recipientCount: comm.numberOfContactsNotified,
            emailCount: contactMethodCounts["email"],
            smsCount: contactMethodCounts["sms"],
            voiceCount: contactMethodCounts["voice"],
            recipients: mappedRecipients,
            associatedEvent: isEmpty(event) ? null : {title: event.title, type: event.type},
            availableEvents: this.mapAvailableEvents(availableEvents),
        };
        return mappedComm;
    };

    mapCommunicationPoll = (poll, recipients) => {
        const {question, responses} = poll;

        const options = _.orderBy(question.options, 'sortOrder', 'asc').map(o => {
            return {text: o.optionText, responseCount: _.filter(responses, ['selectedOptionId', o.id]).length};
        });

        return {
            question: question.questionText,
            options: options,
            noResponseCount: Math.max(recipients.length - responses.length, 0)
        };
    };

    mapCommunicationRecipients = (deliveries, includesPoll, poll) => {
        const uniqueContacts = _.uniqBy(deliveries, 'contactId');
        const sortedContacts = _.orderBy(uniqueContacts, 'displayName', 'asc');
        return sortedContacts.map(contact => {
            const deliveriesPerContact = _.filter(deliveries, {'contactId': contact.contactId});
            const contactResponse = includesPoll ? _.find(poll.responses, {'contactId': contact.contactId}) : null;
            const pollResponseText = isEmpty(contactResponse) ? null :
                _.find(poll.question.options, {'id': contactResponse.selectedOptionId}).optionText;
            const pollResponseDate = isEmpty(contactResponse) ? null : contactResponse.responseDate;

            return {
                id: contact.contactId,
                displayName: contact.displayName,
                notificationMethods: _.map(deliveriesPerContact, 'contactMethodName'),
                pollResponse: pollResponseText,
                pollResponseDate: pollResponseDate,
                pollResponseDateString: isEmpty(pollResponseDate) ? '' : utcToLocalMMDDYYYYWithTime(pollResponseDate)
            };
        });
    };

    mapCommunicationContacts = contactsArray => {
        return isEmpty(contactsArray) ? [] : _.orderBy(contactsArray, 'displayName', 'asc').map(this.mapCommunicationContact);
    };

    mapAvailableEvents = events => {
        return _.orderBy(events, 'dateOfEvent', 'desc').map(e => {
            return {id: e.eventID, title: e.title, type: e.type};
        });
    };

    mapCommunicationContact = contact => {
        const listItem =  {
            id: contact.contactID,
            displayName: contact.displayName,
            location: contact.locationName,
            type: contact.typeName,
            organization: contact.organizationName
        };
        return listItem;
    };

    mapCommunicationsList = communicationsArray => {
        const sortedArray = _.orderBy(communicationsArray, c => { return c.statusString === 'Sent' ? c.sentDate : c.createdDate; }, 'desc');
        return sortedArray.map(this.mapCommunicationsListItem);
    };

    mapCommunicationsListItem = communication => {
        const listItem =  {
            id: communication.id,
            title: communication.title,
            status: communication.statusString,
            dateCreatedString: utcToLocalMMDDYYYY(communication.createdDate),
            sentDateString: utcToLocalMMDDYYYY(communication.sentDate),
            recipientCount: communication.numberOfContactsNotified
        };
        return listItem;
    };

    mapCommunicationDetailedReportData = communications => {
        const sortedComms = _.orderBy(communications, c => { return c.statusString === 'Sent' ? c.sentDate : c.createdDate; }, 'desc');
        return _.flatten(sortedComms.map(this.mapCommunicationDetailedReportRow));
    };

    mapCommunicationDetailedReportRow = comm => {
        const {includesPoll, poll, contacts} = comm;
        const mappedComm = this.mapCommunicationReportData(comm);

        mappedComm.pollQuestion = includesPoll ? poll.question.questionText : '';
        const options = includesPoll ? _.orderBy(poll.question.options, 'sortOrder', 'asc') : [];
        for (let i = 0; i < 6; i++) {
            mappedComm[`pollOptionText_${i + 1}`] = !includesPoll ? '' : i < options.length ? options[i].optionText : 'n/a';
        }

        return _.sortBy(contacts, ['firstName', 'lastName']).map(contact => {
            const rowData = Object.assign(contact, mappedComm);

            rowData.emailSent = contact.contactMethodNames.includes('Email') ? 'Y' : 'N';
            rowData.smsSent = contact.contactMethodNames.includes('Sms') ? 'Y' : 'N';
            rowData.voiceSent = contact.contactMethodNames.includes('VoiceCall') ? 'Y' : 'N';

            const contactResponse = includesPoll ? _.find(poll.responses, ['contactId', contact.contactId]) : null;
            rowData.noResponse = includesPoll && isEmpty(contactResponse) ? 'Y' : '';
            const responseIndex = isEmpty(contactResponse) ? -1 : _.findIndex(options, ['id', contactResponse.selectedOptionId]);
            for (let i = 0; i < 6; i++) {
                let responseVal = responseIndex === i ? 'Y' : '';
                rowData[`pollOptionResponse_${i + 1}`] = !includesPoll ? '' : i < options.length ? responseVal : 'n/a';
            }

            return rowData;
        });
    };

    mapCommunicationSummaryReportData = communications => {
        const sortedArray = _.orderBy(communications, c => { return c.statusString === 'Sent' ? c.sentDate : c.createdDate; }, 'desc');
        return sortedArray.map(this.mapCommunicationSummaryReportRow);
    };

    mapCommunicationSummaryReportRow = c => {
        const {includesPoll, contacts} = c;
        const mappedPoll = includesPoll ? this.mapCommunicationPoll(c.poll, contacts) : null;

        const rowData = this.mapCommunicationReportData(c);
        rowData.pollQuestion = includesPoll ? mappedPoll.question : '';
        rowData.pollNoResponseCount = includesPoll ? mappedPoll.noResponseCount : '';

        for (let i = 0; i < 6; i++) {
            rowData[`pollOptionText_${i + 1}`] = !includesPoll ? '' : i < mappedPoll.options.length ? mappedPoll.options[i].text : 'n/a';
            rowData[`pollOptionResponseCount_${i + 1}`] = !includesPoll ? '' : i < mappedPoll.options.length ? mappedPoll.options[i].responseCount : 'n/a';
        }

        return rowData;
    };

    mapCommunicationReportData = c => {
        const {event, includesPoll} = c;

        return  {
            commTitle: c.title,
            event: isEmpty(event) ? '' : c.event.title,
            eventType: isEmpty(event) ? '' : c.event.type,
            recipientCount: c.numberOfContactsNotified,
            emailSent: c.sentViaEmail ? 'Y' : 'N',
            smsSent: c.sentViaSms ? 'Y' : 'N',
            voiceSent: c.sentViaVoice ? 'Y' : 'N',
            dateCreatedString: utcToLocalMMDDYYYYWithTime(c.createdDate),
            sentDateString: isEmpty(c.sentDate) ? '' : utcToLocalMMDDYYYYWithTime(c.sentDate),
            message: c.messageText,
            includesPoll: includesPoll ? 'Y' : 'N',
        };
    };

    mapPollAssignment = poll => {
        const mappedAssignment = {
            customerName: poll.customerName,
            title: poll.communicationTitle,
            message: poll.communicationMessage,
            question: poll.question.questionText,
            options: _.orderBy(poll.question.options, 'sortOrder', 'asc').map(o => {
                return {text: o.optionText, id: o.id};
            })
        };
        return mappedAssignment;
    };

    mapContactsByGroupData = data => {
        return {
            events: this.mapEventContactData(data.events),
            eventlessFunctions: this.mapFunctionContactData(data.eventlessFunctions)
        };
    };

    mapEventContactData = events => {
        return isEmpty(events) ? [] : _.orderBy(events, 'title', 'asc').map (e => {
            return {
                id: e.eventID,
                title: e.title,
                description: e.description,
                type: e.type,
                functions: this.mapFunctionContactData(e.functions)
            };
        });
    };

    mapFunctionContactData = functions => {
        return isEmpty(functions) ? [] : _.orderBy(functions, 'functionName', 'asc').map(f => {
            return {
                id: f.planID === 0 ? `function_${f.functionID}` : `plan_${f.planID}`,
                name: f.functionName,
                callTrees: this.mapCallTreeContactData(f.callTrees),
                teams: this.mapTeamContactData(f.teams)
            };
        });
    };

    mapCallTreeContactData = callTrees => {
        return isEmpty(callTrees) ? [] : _.orderBy(callTrees, 'callTreeName', 'asc').map(ct => {
            return {
                id: ct.callTreeID,
                name: ct.callTreeName,
                contacts: this.mapCommunicationContacts(ct.contacts)
            };
        });
    };

    mapTeamContactData = teams => {
        return isEmpty(teams) ? [] : _.orderBy(teams, 'teamName', 'asc').map(t => {
            return {
                id: t.functionTeamID,
                name: t.teamName,
                contacts: this.mapCommunicationContacts(t.contacts)
            };
        });
    };
}

const mapper = new ModelMapperService();
export default mapper;
